import type { SupportChatResponse, SupportMessage } from '@/types/support'

export const SUPPORT_CHAT_STORAGE_VERSION = 1
export const SUPPORT_CHAT_TTL_MS = 24 * 60 * 60 * 1000

const storageKey = (userId: string) =>
  `lina_support_chat_v${SUPPORT_CHAT_STORAGE_VERSION}_${userId}`

export interface SupportChatLocalState {
  version: typeof SUPPORT_CHAT_STORAGE_VERSION
  savedAt: string
  userId: string
  sessionId: string | null
  messages: SupportMessage[]
  selectedCategory: string | null
  showCategorySelection: boolean
  ticketSubmitted: boolean
  awaitingTicketConfirmation: boolean
  lastResponse: SupportChatResponse | null
}

function isExpired(savedAt: string): boolean {
  const savedTime = new Date(savedAt).getTime()
  if (Number.isNaN(savedTime)) return true
  return Date.now() - savedTime > SUPPORT_CHAT_TTL_MS
}

export function loadSupportChatState(userId: string): SupportChatLocalState | null {
  if (typeof window === 'undefined' || !userId) return null

  try {
    const raw = localStorage.getItem(storageKey(userId))
    if (!raw) return null

    const parsed = JSON.parse(raw) as SupportChatLocalState
    if (parsed?.version !== SUPPORT_CHAT_STORAGE_VERSION) return null
    if (parsed.userId !== userId) return null
    if (isExpired(parsed.savedAt)) {
      clearSupportChatState(userId)
      return null
    }

    return parsed
  } catch {
    return null
  }
}

export function saveSupportChatState(
  userId: string,
  state: Omit<SupportChatLocalState, 'version' | 'savedAt' | 'userId'>,
): void {
  if (typeof window === 'undefined' || !userId) return

  try {
    const payload: SupportChatLocalState = {
      version: SUPPORT_CHAT_STORAGE_VERSION,
      savedAt: new Date().toISOString(),
      userId,
      ...state,
    }
    localStorage.setItem(storageKey(userId), JSON.stringify(payload))
  } catch (err) {
    console.warn('[support-chat-storage] Failed to persist session', err)
  }
}

export function clearSupportChatState(userId: string): void {
  if (typeof window === 'undefined' || !userId) return

  try {
    localStorage.removeItem(storageKey(userId))
  } catch {
    // ignore
  }
}
